// Layout
import Breadcrumbs from '@/components/ui/Breadcrumbs'
import ScrollableScreen from '@/components/ui/ScrollableScreen'
import NotFound from '@/components/ui/NotFound'

// React
import { useState, useEffect, useRef } from 'react'

// API
import { useAxiosApi } from '@/hooks/useAxiosApi'

// Next
import { useParams } from 'react-router-dom'

// Mantine
import { Menu, ActionIcon } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'

// Custom UI components
import Badge from '@/components/ui/Badge'

// UI
import ProductionOrderProgressBlock from '@/components/production_orders/ProductionOrderProgressBlock'
import ProductionOrderChildrenBlock from '@/components/production_orders/ProductionOrderChildrenBlock'
import ProductionOrderMNPBlock from '@/components/production_orders/ProductionOrderMNPBlock'
import ProductionOrderEditStatus from '@/components/production_orders/ProductionOrderEditStatus'
import ProductionOrderEditQuantity from '@/components/production_orders/ProductionOrderEditQuantity'
import ProductionOrderEditDates from '@/components/production_orders/ProductionOrderEditDates'
import ProductionOrderLocation from '@/components/production_orders/ProductionOrderLocation'
import InvlevelHistory from '@/components/ui/InvlevelHistory'
import Banner from '@/components/ui/Banner'

// Icon
import { DotsVertical } from 'tabler-icons-react'
import { IconAlertCircle, IconHistory } from '@tabler/icons-react'

// Dayjs
import dayjs from 'dayjs'

// User
import useMe from '@/hooks/useMe'

// Production order statuses
import statuses from '@/lib/productionOrderStatuses'

export default function ProductionOrderDisplay() {
    // User
    const { isAdmin } = useMe()

    const [productionOrder, setProductionOrder] = useState(null)

    // Load component
    const { requestGet } = useAxiosApi()

    const { production_order_id } = useParams()

    const loadProductionOrder = async () => {
        const { data } = await requestGet(
            `/production-orders/${production_order_id}`
        )

        setProductionOrder(data)
    }

    // Refetch data
    const invlevelHistoryRef = useRef()

    const refetchProductionOrder = async () => {
        await loadProductionOrder()
        await invlevelHistoryRef.current.fetch()
    }

    useEffect(() => {
        loadProductionOrder()
    }, [production_order_id])

    // Modals
    const [openedEditStatus, handlersEditStatus] = useDisclosure()
    const [openedEditQuantity, handlersEditQuantity] = useDisclosure()
    const [openedEditDates, handlersEditDates] = useDisclosure()

    // Breadcrumbs
    const breadcrumbs = [
        {
            href: `/assembly?status=open,scheduled&orderBy=starting_at&orderByDirection=asc&location_id=${productionOrder?.location_id}`,
            label: 'Assemblages',
        },
        {
            label: `A${productionOrder?.id} - ${productionOrder?.component.name} (${productionOrder?.quantity})`,
        },
    ]

    // Right section
    const rightSection = (
        <div className="flex items-center gap-3">
            <Badge
                color={statuses(productionOrder).badgeColor}
                size="md"
            >
                {statuses(productionOrder).label}
            </Badge>

            <InvlevelHistory
                production_order_id={productionOrder?.id}
                ref={invlevelHistoryRef}
            >
                {(openHistory) => (
                    <Menu position="bottom-end">
                        <Menu.Target>
                            <ActionIcon variant="default" size="lg">
                                <DotsVertical size={16} />
                            </ActionIcon>
                        </Menu.Target>

                        <Menu.Dropdown>
                            {['scheduled', 'open'].includes(productionOrder?.status) && (
                                <>
                                    <Menu.Item onClick={handlersEditDates.open}>
                                        Modifier les dates
                                    </Menu.Item>

                                    <Menu.Item onClick={handlersEditQuantity.open}>
                                        Modifier la quantité
                                    </Menu.Item>
                                </>
                            )}

                            <Menu.Item onClick={handlersEditStatus.open}>
                                Modifier le statut
                            </Menu.Item>

                            <Menu.Item
                                leftSection={<IconHistory size={16} />}
                                onClick={openHistory}
                            >
                                Historique du stock
                            </Menu.Item>
                        </Menu.Dropdown>
                    </Menu>
                )}
            </InvlevelHistory>
        </div>
    )

    // Check if outdated
    const isOutdated =
        productionOrder?.status === 'open' &&
        dayjs(productionOrder?.ending_at).isBefore(dayjs(), 'day')

    // If not admin, it can only see the production order if it's in the location 1, redirect to assembly list
    const isInLocation1 = productionOrder?.location_id === 1

    if (!productionOrder) {
        return null
    }

    if (!isAdmin && !isInLocation1) {
        return <NotFound />
    }

    return (
        <ScrollableScreen
            breadcrumbs={breadcrumbs}
            rightSection={rightSection}
        >
            {productionOrder && (
                <>
                    <ProductionOrderEditStatus
                        {...productionOrder}
                        opened={openedEditStatus}
                        onClose={handlersEditStatus.close}
                        onChanged={loadProductionOrder}
                    />

                    <ProductionOrderEditQuantity
                        {...productionOrder}
                        opened={openedEditQuantity}
                        onClose={handlersEditQuantity.close}
                        onChanged={loadProductionOrder}
                    />

                    <ProductionOrderEditDates
                        {...productionOrder}
                        opened={openedEditDates}
                        onClose={handlersEditDates.close}
                        onChanged={loadProductionOrder}
                    />

                    <div className="flex w-full max-w-[1000px] mx-auto">
                        <div className="flex flex-col gap-5 w-full p-5 mx-auto">
                            {isOutdated && (
                                <Banner
                                    title="Date de fin à ajuster"
                                    color="red"
                                    className="mt-5"
                                    variant="filled"
                                    type="error"
                                    icon={<IconAlertCircle size="1rem" />}
                                >
                                    La date de fin depassée fausse le calcul des
                                    jours restants en stock de{' '}
                                    {productionOrder.component.children.length +
                                        1}{' '}
                                    composants.
                                    <br />
                                    Ajuster la date de fin prévisionelle pour
                                    éviter des erreurs.
                                </Banner>
                            )}

                            <div className="grid grid-cols-1 w-full mx-auto gap-5 items-start relative">
                                <div className="grid grid-cols-1 gap-5">
                                    <ProductionOrderLocation
                                        productionOrder={productionOrder}
                                    />

                                    <ProductionOrderProgressBlock
                                        productionOrder={productionOrder}
                                        onChanged={refetchProductionOrder}
                                    />

                                    <ProductionOrderChildrenBlock
                                        {...productionOrder}
                                    />

                                    <ProductionOrderMNPBlock
                                        productionOrder={productionOrder}
                                        loadProductionOrder={
                                            refetchProductionOrder
                                        }
                                    />

                                    <div className="p-5 bg-white border rounded-sm hidden">
                                        <pre>
                                            {JSON.stringify(
                                                productionOrder,
                                                null,
                                                2
                                            )}
                                        </pre>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </ScrollableScreen>
    )
}
/*
export const getServerSideProps = async ({ ctx, query }) => {
    const { production_order_id } = query

    return {
        props: {
            production_order_id,
        },
    }
}*/
